import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useReplayEngine from '../hooks/useReplayEngine';
import useIndicators from '../hooks/useIndicators';
import useTradingEngine from '../hooks/useTradingEngine';
import CandlestickChart from '../components/CandlestickChart';
import ReplayControls from '../components/ReplayControls';
import IndicatorSelector from '../components/IndicatorSelector';
import MultiTimeframeView, { TimeframeSelector } from '../components/MultiTimeframeView';
import OrderPanel from '../components/OrderPanel';
import PositionsList from '../components/PositionsList';
import CapitalDisplay from '../components/CapitalDisplay';
import DrawingSidebar from '../components/DrawingSidebar';
import useDrawingTools from '../hooks/useDrawingTools';
import TemplateManager from '../components/TemplateManager';
import AIAnalysisPanel from '../components/AIAnalysisPanel';
import PlaybookSidebar from '../components/PlaybookSidebar';
import CorrelationSelector from '../components/CorrelationSelector';
import useCorrelationPairs from '../hooks/useCorrelationPairs';
import usePropFirmChallenge from '../hooks/usePropFirmChallenge';
import PerformanceDashboard from '../components/PerformanceDashboard';
import ChallengeHUD from '../components/ChallengeHUD';
import useEconomicNews from '../hooks/useEconomicNews';
import useAIAnalysis from '../hooks/useAIAnalysis';
import usePriceAlerts from '../hooks/usePriceAlerts';
import { sessions as sessionsApi, playbooks as playbooksApi } from '../api/client';
import { detectDivergences } from '../utils/divergenceDetector';
import StrategyTester from '../components/StrategyTester';

function AlertBuilder({ onSave, onCancel }) {
  const [label, setLabel] = useState('');
  const [logic, setLogic] = useState('AND');
  const [conditions, setConditions] = useState([
    { type: 'price', source: 'price', operator: '>', value: '' },
  ]);
  const addCond = () => setConditions(p => [...p, { type: 'price', source: 'price', operator: '>', value: '' }]);
  const removeCond = (i) => setConditions(p => p.filter((_, idx) => idx !== i));
  const updateCond = (i, field, val) => setConditions(p => p.map((c, idx) => idx === i ? { ...c, [field]: val } : c));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.5rem' }}>
      <div className="order-field">
        <label>Alert Name</label>
        <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="My Alert" />
      </div>
      <div className="order-field">
        <label>Logic</label>
        <select value={logic} onChange={e => setLogic(e.target.value)}>
          <option value="AND">AND — all conditions must be true</option>
          <option value="OR">OR — any condition triggers</option>
        </select>
      </div>
      {conditions.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={`${c.type}:${c.source || ''}`} onChange={e => {
            const [type, source] = e.target.value.split(':');
            updateCond(i, 'type', type);
            updateCond(i, 'source', source || 'price');
          }} style={{ width: 120 }}>
            <optgroup label="Price">
              <option value="price:close">Price (Close)</option>
              <option value="price:high">Price (High)</option>
              <option value="price:low">Price (Low)</option>
              <option value="price:open">Price (Open)</option>
            </optgroup>
            <optgroup label="Oscillators">
              <option value="indicator:rsi">RSI</option>
              <option value="indicator:stoch_k">Stochastic %K</option>
              <option value="indicator:cci">CCI</option>
              <option value="indicator:mfi">MFI</option>
              <option value="indicator:williams_r">Williams %R</option>
              <option value="indicator:roc">ROC</option>
              <option value="indicator:momentum">Momentum</option>
            </optgroup>
            <optgroup label="Trend">
              <option value="indicator:macd">MACD</option>
              <option value="indicator:macd_signal">MACD Signal</option>
              <option value="indicator:macd_hist">MACD Histogram</option>
              <option value="indicator:adx">ADX</option>
              <option value="indicator:ao">Awesome Oscillator</option>
            </optgroup>
            <optgroup label="Moving Averages">
              <option value="indicator:sma_20">SMA 20</option>
              <option value="indicator:sma_50">SMA 50</option>
              <option value="indicator:sma_200">SMA 200</option>
              <option value="indicator:ema_9">EMA 9</option>
              <option value="indicator:ema_21">EMA 21</option>
              <option value="indicator:ema_50">EMA 50</option>
            </optgroup>
            <optgroup label="Volatility">
              <option value="indicator:atr">ATR</option>
              <option value="indicator:bb_upper">BB Upper</option>
              <option value="indicator:bb_lower">BB Lower</option>
            </optgroup>
            <optgroup label="Volume">
              <option value="indicator:obv">OBV</option>
              <option value="indicator:cmf">CMF</option>
              <option value="price:volume">Volume</option>
            </optgroup>
          </select>
          <select value={c.operator} onChange={e => updateCond(i, 'operator', e.target.value)} style={{ width: 110 }}>
            <option value=">">Greater than</option>
            <option value="<">Less than</option>
            <option value=">=">Greater or equal</option>
            <option value="<=">Less or equal</option>
            <option value="crossover">Crosses Above</option>
            <option value="crossunder">Crosses Below</option>
          </select>
          <input type="number" step="any" value={c.value} onChange={e => updateCond(i, 'value', e.target.value)}
            placeholder="Value" style={{ width: 90 }} />
          <button className="st-remove" onClick={() => removeCond(i)}>✕</button>
        </div>
      ))}
      <button className="st-add-btn" onClick={addCond} style={{ alignSelf: 'flex-start' }}>+ Add Condition</button>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button className="btn btn-primary btn-sm" onClick={() => onSave(label || 'Alert', conditions, logic)}>Create Alert</button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function ReplayPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const chartContainerRef = useRef(null);
  const [selectedTfs, setSelectedTfs] = useState([]);
  const [settingsPanelId, setSettingsPanelId] = useState(null);
  const [showVolume, setShowVolume] = useState(true);
  const [chartType, setChartType] = useState('candles');
  const [showNews, setShowNews] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(true);
  const [activePlaybookId, setActivePlaybookId] = useState(null);
  const [showCorrelation, setShowCorrelation] = useState(false);
  const [logScale, setLogScale] = useState(false);
  const [showVP, setShowVP] = useState(false);
  const [showMP, setShowMP] = useState(false);
  const [correlationIds, setCorrelationIds] = useState([]);
  const [crosshairSyncTime, setCrosshairSyncTime] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [showAlertBuilder, setShowAlertBuilder] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [showStrategyTester, setShowStrategyTester] = useState(false);
  const [tzOffset, setTzOffset] = useState(() => parseInt(localStorage.getItem('tradrin-tz-offset') || '0'));
  const [watermarkOn, setWatermarkOn] = useState(() => localStorage.getItem('tradrin-wm-on') !== 'false');
  const [watermarkSize, setWatermarkSize] = useState(() => parseInt(localStorage.getItem('tradrin-wm-size') || '5'));
  const [watermarkOpacity, setWatermarkOpacity] = useState(() => parseInt(localStorage.getItem('tradrin-wm-opacity') || '15'));
  const [watermarkColor, setWatermarkColor] = useState(() => localStorage.getItem('tradrin-wm-color') || '#6b7280');
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [showCorrSelector, setShowCorrSelector] = useState(false);
  const [playbooksList, setPlaybooksList] = useState([]);

  // Load playbooks
  useState(() => {
    playbooksApi.list().then(res => setPlaybooksList(res.data)).catch(() => {});
  }, []);

  const toggleTf = useCallback((tf) => {
    setSelectedTfs((prev) => prev.includes(tf) ? prev.filter((t) => t !== tf) : [...prev, tf]);
  }, []);
  const reorderTfs = useCallback((fromTf, toTf) => {
    setSelectedTfs((prev) => {
      const arr = [...prev];
      const fromIdx = arr.indexOf(fromTf);
      const toIdx = arr.indexOf(toTf);
      if (fromIdx === -1 || toIdx === -1) return prev;
      arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, fromTf);
      return arr;
    });
  }, []);

  const {
    allCandles, visibleCandles, currentIndex, totalBars,
    speed, speeds, playing, loading, session, atEnd,
    play, pause, stepForward, stepBackward, skipForward, skipBackward, changeSpeed, goToIndex,
  } = useReplayEngine(sessionId);

  // Apply timezone offset to candle timestamps for chart display
  const tzVisibleCandles = useMemo(() => {
    if (!tzOffset) return visibleCandles;
    const off = tzOffset * 3600;
    return visibleCandles.map(c => ({ ...c, time: c.time + off }));
  }, [visibleCandles, tzOffset]);

  const drawingTools = useDrawingTools(chartContainerRef, visibleCandles, sessionId, session?.drawing_config);
  const { events: newsEvents } = useEconomicNews(session?.instrument, visibleCandles, showNews);

  const {
    activeIndicators, overlayIndicators, panelIndicators,
    addIndicator, removeIndicator, updateIndicatorParams, setIndicators,
    flushSave: flushIndicatorSave,
  } = useIndicators(tzVisibleCandles, sessionId, session?.indicator_config);

  const {
    openPositions, pendingOrders, closedTrades,
    currentCapital, totalUnrealizedPnl, equity,
    placeMarketOrder, placePendingOrder, cancelPendingOrder,
    closePosition, modifyPosition, closeAllPositions, computeSessionStats,
  } = useTradingEngine(sessionId, session, currentIndex, allCandles, chartContainerRef);

  const aiAnalysis = useAIAnalysis(visibleCandles, activeIndicators, openPositions, session);
  const syncedPairs = useCorrelationPairs(visibleCandles, correlationIds);
  const challenge = usePropFirmChallenge(session, equity, session?.starting_capital, allCandles, currentIndex);

  const currentPrice = visibleCandles.length > 0
    ? visibleCandles[visibleCandles.length - 1].close : null;

  const priceAlerts = usePriceAlerts(currentPrice);

  const tradeMarkers = useMemo(() => {
    const markers = [];
    const off = tzOffset * 3600;
    const addMarker = (ts, dir, type) => {
      const time = (typeof ts === 'string' ? Math.floor(new Date(ts).getTime() / 1000) : ts) + off;
      if (type === 'entry') {
        markers.push({ time, position: dir === 'buy' ? 'belowBar' : 'aboveBar',
          color: dir === 'buy' ? '#22c55e' : '#ef4444',
          shape: dir === 'buy' ? 'arrowUp' : 'arrowDown', text: dir === 'buy' ? 'B' : 'S' });
      } else {
        markers.push({ time, position: 'aboveBar', color: '#8b8fa3', shape: 'circle', text: 'X' });
      }
    };
    closedTrades.forEach((t) => { addMarker(t.entry_timestamp, t.direction, 'entry'); if (t.exit_timestamp) addMarker(t.exit_timestamp, t.direction, 'exit'); });
    openPositions.forEach((p) => addMarker(p.entry_timestamp, p.direction, 'entry'));

    return markers;
  }, [openPositions, closedTrades, tzOffset]);

  const handlePriceLineDrag = useCallback((title, newPrice) => {
    // Find the position this line belongs to
    for (const pos of openPositions) {
      if (title === 'SL' && pos.stop_loss != null) {
        modifyPosition(pos.id, { stopLoss: newPrice });
        return;
      }
      if (title === 'TP' && pos.take_profit != null) {
        modifyPosition(pos.id, { takeProfit: newPrice });
        return;
      }
    }
  }, [openPositions, modifyPosition]);

  const priceLines = useMemo(() => {
    const lines = [];
    openPositions.forEach((pos) => {
      if (pos.stop_loss != null) lines.push({ price: pos.stop_loss, color: '#ef4444', title: 'SL', lineStyle: 2, posId: pos.id });
      if (pos.take_profit != null) lines.push({ price: pos.take_profit, color: '#22c55e', title: 'TP', lineStyle: 2, posId: pos.id });
    });
    // Add active alert lines
    priceAlerts.alerts.forEach(a => {
      if (a.active) lines.push({ price: a.price, color: '#f59e0b', title: `Alert`, lineStyle: 1 });
    });
    return lines;
  }, [openPositions, priceAlerts.alerts]);

  const handlePlaceOrder = (order) => {
    if (order.type === 'market') placeMarketOrder(order.direction, order.volume, order.stopLoss, order.takeProfit, order.playbookId, order.trailingDistance);
    else placePendingOrder(order.type, order.direction, order.price, order.volume, order.stopLoss, order.takeProfit, order.playbookId);
  };

  const handleComplete = async () => {
    if (openPositions.length > 0) {
      if (!window.confirm('You have open positions. Close all and end session?')) return;
      await closeAllPositions();
    } else if (!window.confirm('End this session?')) return;
    try {
      const stats = computeSessionStats();
      await flushIndicatorSave();
      await sessionsApi.complete(sessionId, { final_capital: Math.round((currentCapital + totalUnrealizedPnl) * 100) / 100, stats });
      navigate('/');
    } catch { alert('Failed to complete session.'); }
  };

  const handleAbandon = async () => {
    if (!window.confirm('Abandon this session?')) return;
    try { await flushIndicatorSave(); await sessionsApi.abandon(sessionId); navigate('/'); }
    catch { alert('Failed to abandon session.'); }
  };

  const handleLoadTemplate = useCallback((template) => {
    if (template.chartType) setChartType(template.chartType);
    if (template.showVolume !== undefined) setShowVolume(template.showVolume);
    if (template.selectedTfs) setSelectedTfs(template.selectedTfs);
    if (template.activeIndicators) setIndicators(template.activeIndicators);
    if (template.drawings?.length > 0) drawingTools.loadDrawingTemplate(template.drawings);
  }, [setIndicators, drawingTools.loadDrawingTemplate]);

  // ── Hotkeys ──
  const [showHotkeys, setShowHotkeys] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      // Ctrl/Meta combos are handled elsewhere (undo/redo)
      if (e.ctrlKey || e.metaKey) return;

      switch (e.key) {
        case ' ': // Space = play/pause
          e.preventDefault();
          playing ? pause() : play();
          break;
        case 'ArrowRight': // Step forward
          e.preventDefault();
          if (e.shiftKey) skipForward(50);
          else stepForward();
          break;
        case 'ArrowLeft': // Step backward
          e.preventDefault();
          if (e.shiftKey) skipBackward(50);
          else stepBackward();
          break;
        case 'b': case 'B': // Buy market
          if (currentPrice) placeMarketOrder('buy', null, null, null, activePlaybookId);
          break;
        case 's': case 'S': // Sell market
          if (currentPrice) placeMarketOrder('sell', null, null, null, activePlaybookId);
          break;
        case 'x': case 'X': // Close all positions
          if (openPositions.length > 0) closeAllPositions();
          break;
        case '?': // Show hotkeys help
          setShowHotkeys(v => !v);
          break;
        default: break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [playing, play, pause, stepForward, stepBackward, skipForward, skipBackward, currentPrice, placeMarketOrder, closeAllPositions, openPositions, activePlaybookId]);

  const saveLayout = useCallback(() => {
    const name = prompt('Layout name:');
    if (!name) return;
    const layout = {
      name,
      chartType, showVolume, logScale, showVP, showMP, showNews, showAI,
      selectedTfs,
      activeIndicators: activeIndicators.map(({ indicatorId, instanceId, params }) => ({ indicatorId, instanceId, params })),
      drawings: drawingTools.getSerializedDrawings(),
      date: new Date().toISOString(),
    };
    const layouts = JSON.parse(localStorage.getItem('chartLayouts') || '[]');
    layouts.push(layout);
    localStorage.setItem('chartLayouts', JSON.stringify(layouts));
  }, [chartType, showVolume, logScale, showVP, showMP, showNews, showAI, selectedTfs, activeIndicators, drawingTools]);

  const loadLayout = useCallback((layout) => {
    if (layout.chartType) setChartType(layout.chartType);
    if (layout.showVolume !== undefined) setShowVolume(layout.showVolume);
    if (layout.logScale !== undefined) setLogScale(layout.logScale);
    if (layout.showVP !== undefined) setShowVP(layout.showVP);
    if (layout.showMP !== undefined) setShowMP(layout.showMP);
    if (layout.showNews !== undefined) setShowNews(layout.showNews);
    if (layout.showAI !== undefined) setShowAI(layout.showAI);
    if (layout.selectedTfs) setSelectedTfs(layout.selectedTfs);
    if (layout.activeIndicators) setIndicators(layout.activeIndicators);
    if (layout.drawings?.length > 0) {
      // Delay drawing load to allow indicator panels to render first
      setTimeout(() => drawingTools.loadDrawingTemplate(layout.drawings), 500);
    }
    setShowLayoutMenu(false);
  }, [setIndicators, drawingTools]);

  const [layoutVersion, setLayoutVersion] = useState(0);
  const deleteLayout = useCallback((idx) => {
    const layouts = JSON.parse(localStorage.getItem('chartLayouts') || '[]');
    layouts.splice(idx, 1);
    localStorage.setItem('chartLayouts', JSON.stringify(layouts));
    setLayoutVersion(v => v + 1);
  }, []);

  const savedLayouts = showLayoutMenu ? JSON.parse(localStorage.getItem('chartLayouts') || '[]') : [];
  void layoutVersion; // ensure re-render on delete

  const saveSnapshot = useCallback(() => {
    setSnapshots(prev => [...prev, {
      id: Date.now(),
      index: currentIndex,
      time: visibleCandles.length > 0 ? new Date(visibleCandles[visibleCandles.length - 1].time * 1000).toLocaleString() : '',
    }]);
  }, [currentIndex, visibleCandles]);

  const restoreSnapshot = useCallback((snap) => {
    goToIndex(snap.index);
  }, [goToIndex]);

  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [bottomPanelOpen, setBottomPanelOpen] = useState(true);
  const [bottomTab, setBottomTab] = useState('positions');

  if (loading) return <div className="page">Loading replay data...</div>;
  if (!session) return <div className="page"><div className="error-msg">Session not found.</div></div>;

  const progress = totalBars > 0 ? ((currentIndex / totalBars) * 100).toFixed(1) : 0;

  return (
    <div className="replay-page">
      {/* ── TOPBAR: 36px single line ── */}
      <div className="replay-topbar">
        <div className="tb-progress" style={{ width: `${progress}%` }} />

        {/* Left: identity */}
        <div className="tb-section">
          <span className="tb-instrument">{session.instrument}</span>
          <span className="tb-session-name">{session.name || `#${session.id}`}</span>
          <TimeframeSelector baseTimeframe={session.timeframe} selectedTfs={selectedTfs} onToggleTf={toggleTf} />
        </div>

        {/* Center: transport */}
        <div className="tb-section tb-section-center tb-transport">
          <button className="tb-icon-btn" onClick={() => skipBackward(50)} disabled={playing || currentIndex <= 1} title="Skip back 50">⏪</button>
          <button className="tb-icon-btn" onClick={stepBackward} disabled={playing || currentIndex <= 1} title="Step back">⏮</button>
          {playing
            ? <button className="tb-icon-btn tb-play-btn" onClick={pause} title="Pause">⏸</button>
            : <button className="tb-icon-btn tb-play-btn" onClick={play} disabled={atEnd} title="Play">▶</button>
          }
          <button className="tb-icon-btn" onClick={stepForward} disabled={playing || atEnd} title="Step forward">⏭</button>
          <button className="tb-icon-btn" onClick={() => skipForward(50)} disabled={atEnd} title="Skip forward 50">⏩</button>
          <div className="tb-speed-group">
            {speeds.map(s => <button key={s} className={`tb-speed-btn ${speed === s ? 'active' : ''}`} onClick={() => changeSpeed(s)}>{s}x</button>)}
          </div>
          <span className="tb-bar-counter">{currentIndex}/{totalBars}</span>
        </div>

        {/* Right: capital + tools dropdown + panel toggle + session */}
        <div className="tb-section">
          <CapitalDisplay currentCapital={currentCapital} unrealizedPnl={totalUnrealizedPnl} equity={equity} />
          <div className="tb-divider" />

          {/* Tools dropdown — horizontal flyout */}
          <div style={{ position: 'relative' }}>
            <button className="tb-icon-btn" onClick={() => setShowLayoutMenu(v => v === 'tools' ? false : 'tools')} title="Chart tools">
              <span className="tb-icon-text">Tools ▾</span>
            </button>
            {showLayoutMenu === 'tools' && (<>
              <div className="tb-flyout-backdrop" onClick={() => setShowLayoutMenu(false)} />
              <div className="tb-flyout">
                <div className="tb-flyout-section">
                  <span className="tb-flyout-label">Chart</span>
                  <button className={`tb-flyout-btn ${logScale ? 'active' : ''}`} onClick={() => setLogScale(v => !v)}>Log</button>
                  <button className={`tb-flyout-btn ${showVP ? 'active' : ''}`} onClick={() => setShowVP(v => !v)}>VP</button>
                  <button className={`tb-flyout-btn ${showMP ? 'active' : ''}`} onClick={() => setShowMP(v => !v)}>MP</button>
                  <button className={`tb-flyout-btn ${activeIndicators.some(i => i.indicatorId === 'sessions') ? 'active' : ''}`} onClick={() => {
                    const existing = activeIndicators.find(i => i.indicatorId === 'sessions');
                    if (existing) removeIndicator(existing.instanceId);
                    else addIndicator({ indicatorId: 'sessions', instanceId: `sessions_${Date.now()}`, params: { showLabels: 1, lineWidth: 2 } });
                  }}>Sessions</button>
                </div>
                <div className="tb-flyout-sep" />
                <div className="tb-flyout-section">
                  <span className="tb-flyout-label">Watermark</span>
                  <button className={`tb-flyout-btn ${watermarkOn ? 'active' : ''}`} onClick={() => { setWatermarkOn(v => { localStorage.setItem('tradrin-wm-on', !v); return !v; }); }}>
                    {watermarkOn ? 'On' : 'Off'}
                  </button>
                  {watermarkOn && <>
                    <input type="color" value={watermarkColor} onChange={(e) => { setWatermarkColor(e.target.value); localStorage.setItem('tradrin-wm-color', e.target.value); }} style={{ width: 22, height: 22, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 4, background: 'transparent' }} title="Color" />
                    <button className="tb-flyout-btn" onClick={() => { const v = Math.min(watermarkSize + 1, 12); setWatermarkSize(v); localStorage.setItem('tradrin-wm-size', v); }} title="Bigger">A+</button>
                    <button className="tb-flyout-btn" onClick={() => { const v = Math.max(watermarkSize - 1, 2); setWatermarkSize(v); localStorage.setItem('tradrin-wm-size', v); }} title="Smaller">A-</button>
                    <button className="tb-flyout-btn" onClick={() => { const v = Math.min(watermarkOpacity + 4, 30); setWatermarkOpacity(v); localStorage.setItem('tradrin-wm-opacity', v); }} title="More visible">+</button>
                    <button className="tb-flyout-btn" onClick={() => { const v = Math.max(watermarkOpacity - 4, 2); setWatermarkOpacity(v); localStorage.setItem('tradrin-wm-opacity', v); }} title="Less visible">-</button>
                  </>}
                </div>
                <div className="tb-flyout-sep" />
                <div className="tb-flyout-section">
                  <span className="tb-flyout-label">Timezone</span>
                  <select value={tzOffset} onChange={(e) => { const v = parseInt(e.target.value); setTzOffset(v); localStorage.setItem('tradrin-tz-offset', v); }}
                    className="tb-tz-select">
                    <option value="0">UTC</option>
                    <option value="1">UTC+1 (CET)</option>
                    <option value="2">UTC+2 (EET)</option>
                    <option value="3">UTC+3 (Moscow)</option>
                    <option value="4">UTC+4 (Dubai)</option>
                    <option value="5">UTC+5</option>
                    <option value="5.5">UTC+5:30 (India)</option>
                    <option value="8">UTC+8 (Singapore)</option>
                    <option value="9">UTC+9 (Tokyo)</option>
                    <option value="10">UTC+10 (Sydney)</option>
                    <option value="-3">UTC-3 (Brazil)</option>
                    <option value="-4">UTC-4 (EDT)</option>
                    <option value="-5">UTC-5 (EST/CDT)</option>
                    <option value="-6">UTC-6 (CST)</option>
                    <option value="-7">UTC-7 (MST)</option>
                    <option value="-8">UTC-8 (PST)</option>
                  </select>
                </div>
                <div className="tb-flyout-sep" />
                <div className="tb-flyout-section">
                  <span className="tb-flyout-label">Analysis</span>
                  <button className={`tb-flyout-btn ${showAI ? 'active' : ''}`} onClick={() => setShowAI(v => !v)}>AI</button>
                  <button className={`tb-flyout-btn ${correlationIds.length > 0 ? 'active' : ''}`} onClick={() => { setShowCorrSelector(v => !v); setShowLayoutMenu(false); }}>Corr</button>
                  <button className="tb-flyout-btn" onClick={() => { setShowStrategyTester(true); setShowLayoutMenu(false); }}>Backtest</button>
                </div>
                <div className="tb-flyout-sep" />
                <div className="tb-flyout-section">
                  <span className="tb-flyout-label">Actions</span>
                  <button className="tb-flyout-btn" onClick={() => { const c = chartContainerRef.current?.querySelector('canvas'); if(c){const a=document.createElement('a');a.download='chart.png';a.href=c.toDataURL();a.click();} setShowLayoutMenu(false); }}>Screenshot</button>
                  <button className="tb-flyout-btn" onClick={() => { setShowHotkeys(true); setShowLayoutMenu(false); }}>Hotkeys</button>
                  <button className="tb-flyout-btn" onClick={() => { saveSnapshot(); setShowLayoutMenu(false); }}>
                    Snapshot{snapshots.length > 0 ? ` (${snapshots.length})` : ''}
                  </button>
                </div>
                {snapshots.length > 0 && <>
                  <div className="tb-flyout-sep" />
                  <div className="tb-flyout-section">
                    <span className="tb-flyout-label">Restore</span>
                    {snapshots.map(s => <button key={s.id} className="tb-flyout-btn" onClick={() => { restoreSnapshot(s); setShowLayoutMenu(false); }}>Bar {s.index}</button>)}
                  </div>
                </>}
                <div className="tb-flyout-sep" />
                <div className="tb-flyout-section">
                  <span className="tb-flyout-label">Layouts</span>
                  <button className="tb-flyout-btn" onClick={() => { saveLayout(); setShowLayoutMenu(false); }}>Save</button>
                  {savedLayouts.map((l, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <button className="tb-flyout-btn" style={{ flex: 1 }} onClick={() => loadLayout(l)}>{l.name}</button>
                      <button className="tb-flyout-btn" style={{ color: '#ef4444', padding: '2px 6px', minWidth: 'auto' }}
                        onClick={(e) => { e.stopPropagation(); deleteLayout(i); }} title="Delete">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            </>)}
          </div>

          <TemplateManager
            chartType={chartType} showVolume={showVolume}
            selectedTfs={selectedTfs} activeIndicators={activeIndicators}
            drawings={drawingTools.getSerializedDrawings()}
            onLoad={handleLoadTemplate}
          />

          <button className="tb-icon-btn" onClick={() => setRightPanelOpen(v => !v)} title={rightPanelOpen ? 'Hide order panel' : 'Show order panel'}>
            <span className="tb-icon-text">{rightPanelOpen ? '◧' : '◨'}</span>
          </button>
          <div className="tb-divider" />
          <button className="tb-end-btn" onClick={handleComplete}>End</button>
          <button className="tb-abandon-btn" onClick={handleAbandon}>✕</button>
        </div>
      </div>

      {challenge.isChallenge && <ChallengeHUD challenge={challenge} />}

      {/* ── BODY ── */}
      <div className="replay-body">
        <DrawingSidebar activeTool={drawingTools.activeTool} onSelectTool={drawingTools.selectTool}
          onCancel={drawingTools.cancelTool} onClearAll={drawingTools.clearAll}
          drawings={drawingTools.drawings} onRemoveDrawing={drawingTools.removeDrawing}
          drawingColor={drawingTools.drawingColor} onColorChange={drawingTools.setDrawingColor}
          pendingPoint={drawingTools.pendingPoint} pendingPoints={drawingTools.pendingPoints}
          selectedId={drawingTools.selectedId} onUpdateLevels={drawingTools.updateDrawingLevels}
          onGetLevels={drawingTools.getDrawingLevels} magnetMode={drawingTools.magnetMode}
          onMagnetChange={drawingTools.setMagnetMode} onToggleLock={drawingTools.toggleLock}
          onLoadDrawingTemplate={drawingTools.loadDrawingTemplate} />

        <div className="replay-center">
          {/* Chart */}
          <div className="replay-charts-zone" style={{ position: 'relative' }}
            onContextMenu={(e) => { if (drawingTools.activeTool) return; e.preventDefault();
              const rect = e.currentTarget.getBoundingClientRect();
              const series = chartContainerRef.current?.getCandleSeries?.();
              const price = series ? series.coordinateToPrice(e.clientY - rect.top) : null;
              setContextMenu({ x: e.clientX - rect.left, y: e.clientY - rect.top, price });
            }} onClick={() => contextMenu && setContextMenu(null)}>
            <MultiTimeframeView visibleCandles={tzVisibleCandles} baseTimeframe={session.timeframe}
              selectedTfs={selectedTfs} onToggleTf={toggleTf} onReorderTfs={reorderTfs}
              showVolume={showVolume} onToggleVolume={() => setShowVolume(v => !v)}
              chartType={chartType} onChartTypeChange={setChartType}
              showNews={showNews} onToggleNews={() => setShowNews(v => !v)}
              correlationPairs={syncedPairs}
              onRemoveCorrelation={(id) => setCorrelationIds(prev => prev.filter(x => x !== id))}
              correlationLoading={correlationIds.length > 0 && Object.keys(syncedPairs).length < correlationIds.length}
              onCrosshairSync={(time) => setCrosshairSyncTime(time)}
              primaryChart={
                <CandlestickChart ref={chartContainerRef} candles={tzVisibleCandles} height={300}
                  showVolume={showVolume} chartType={chartType} overlayIndicators={overlayIndicators}
                  panelIndicators={panelIndicators} logScale={logScale}
                  onCrosshairSync={(time) => setCrosshairSyncTime(time)} syncCrosshairTime={crosshairSyncTime}
                  showVolumeProfile={showVP} showMarketProfile={showMP} tzOffset={tzOffset}
                  watermark={watermarkOn ? (session?.instrument || '') : ''}
                  watermarkSize={watermarkSize} watermarkOpacity={watermarkOpacity} watermarkColor={watermarkColor}
                  tradeMarkers={tradeMarkers} priceLines={priceLines} onPriceLineDrag={handlePriceLineDrag}
                  onChartClick={drawingTools.handleChartClick} onChartMouseMove={drawingTools.handleChartMouseMove}
                  onChartDblClick={drawingTools.handleChartDblClick}
                  onRemoveIndicator={removeIndicator}
                  onOpenIndicatorSettings={(id) => setSettingsPanelId(settingsPanelId === id ? null : id)}
                  newsEvents={showNews ? newsEvents : []} />
              }
              indicatorSelector={
                <IndicatorSelector activeIndicators={activeIndicators}
                  onAdd={addIndicator} onRemove={removeIndicator} onUpdate={updateIndicatorParams}
                  editingId={settingsPanelId} onEditingChange={setSettingsPanelId} />
              } />

            {/* Text editor overlay with formatting toolbar */}
            {drawingTools.editingId != null && (() => {
              const d = drawingTools.drawings.find(d => d.id === drawingTools.editingId);
              const sb = d?.primitive?.getScreenBounds?.();
              if (!d || !sb) return null;
              const prim = d.primitive;
              return <div className="text-editor-overlay" style={{ position: 'absolute', left: sb.x, top: Math.max(0, sb.y - 32), zIndex: 50 }} onClick={(e) => e.stopPropagation()}>
                <div className="text-editor-toolbar">
                  <select value={prim.fontSize || 11} onChange={(e) => drawingTools.updateEditingStyle({ fontSize: parseInt(e.target.value) })}>
                    {[8,9,10,11,12,14,16,18,20,24,28,32,36,48].map(s => <option key={s} value={s}>{s}px</option>)}
                  </select>
                  <button className={`te-btn ${prim.bold ? 'te-btn-active' : ''}`} title="Bold" onClick={() => drawingTools.updateEditingStyle({ bold: !prim.bold })}><b>B</b></button>
                  <input type="color" value={prim.color || '#cdd6f4'} onChange={(e) => drawingTools.updateEditingStyle({ color: e.target.value })} title="Text color" className="te-color" />
                  {prim.showBg !== undefined && <button className={`te-btn ${prim.showBg ? 'te-btn-active' : ''}`} title="Background" onClick={() => drawingTools.updateEditingStyle({ showBg: !prim.showBg })}>Bg</button>}
                  <button className="te-btn te-btn-done" title="Done (Ctrl+Enter)" onClick={() => { const ta = document.querySelector('.text-editor-textarea'); drawingTools.finishEditingText(ta?.value ?? prim.text); }}>OK</button>
                </div>
                <textarea autoFocus defaultValue={prim.text} className="text-editor-textarea"
                  style={{ width: Math.max(sb.w, 120), height: Math.max(sb.h, 40), color: prim.color || 'var(--text-bright)', fontSize: (prim.fontSize || 11) + 'px', fontWeight: prim.bold ? 'bold' : 'normal' }}
                  onBlur={(e) => { if (e.relatedTarget?.closest('.text-editor-overlay')) return; drawingTools.finishEditingText(e.target.value); }}
                  onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); drawingTools.finishEditingText(null); } if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); drawingTools.finishEditingText(e.target.value); } e.stopPropagation(); }} />
              </div>;
            })()}

            {/* Drawing properties editor */}
            {drawingTools.propsEditorId != null && (() => {
              const props = drawingTools.getDrawingProps(drawingTools.propsEditorId);
              if (!props) return null;
              return (
                <div className="drawing-props-editor" onClick={(e) => e.stopPropagation()}>
                  <div className="drawing-props-header">
                    <strong>{props.label || props.type}</strong>
                    <button onClick={() => drawingTools.closePropsEditor()}>&times;</button>
                  </div>
                  {props.price !== undefined && (
                    <div className="drawing-props-row">
                      <label>Price</label>
                      <input type="number" step="any" defaultValue={props.price}
                        className="drawing-props-price"
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!isNaN(v)) drawingTools.updateDrawingProps(drawingTools.propsEditorId, { price: v });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.target.blur(); }
                          e.stopPropagation();
                        }} />
                    </div>
                  )}
                  <div className="drawing-props-row">
                    <label>Color</label>
                    <input type="color" value={props.color}
                      onChange={(e) => drawingTools.updateDrawingProps(drawingTools.propsEditorId, { color: e.target.value })} />
                  </div>
                  <div className="drawing-props-row">
                    <label>Width</label>
                    <input type="range" min="1" max="5" step="1" value={props.lineWidth}
                      onChange={(e) => drawingTools.updateDrawingProps(drawingTools.propsEditorId, { lineWidth: parseInt(e.target.value) })} />
                    <span>{props.lineWidth}px</span>
                  </div>
                  <div className="drawing-props-row">
                    <label>Style</label>
                    <div className="drawing-props-styles">
                      {[{ v: 0, l: 'Solid' }, { v: 1, l: 'Dashed' }, { v: 2, l: 'Dotted' }].map(s => (
                        <button key={s.v} className={props.lineStyle === s.v ? 'active' : ''}
                          onClick={() => drawingTools.updateDrawingProps(drawingTools.propsEditorId, { lineStyle: s.v })}>{s.l}</button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Context Menu */}
            {contextMenu && (
              <div className="chart-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
                {contextMenu.price && <div className="ctx-price">{contextMenu.price.toFixed(5)}</div>}
                <button onClick={() => { navigator.clipboard.writeText(contextMenu.price?.toFixed(5) || ''); setContextMenu(null); }}>Copy Price</button>
                <div className="ctx-sep" />
                <button onClick={() => { if (currentPrice) placeMarketOrder('buy', null, null, null, activePlaybookId); setContextMenu(null); }}>Buy Market</button>
                <button onClick={() => { if (currentPrice) placeMarketOrder('sell', null, null, null, activePlaybookId); setContextMenu(null); }}>Sell Market</button>
                {contextMenu.price && <>
                  <button onClick={() => { placePendingOrder('limit', 'buy', contextMenu.price, null, null, null, activePlaybookId); setContextMenu(null); }}>Buy Limit @ {contextMenu.price.toFixed(2)}</button>
                  <button onClick={() => { placePendingOrder('limit', 'sell', contextMenu.price, null, null, null, activePlaybookId); setContextMenu(null); }}>Sell Limit @ {contextMenu.price.toFixed(2)}</button>
                </>}
                {openPositions.length > 0 && <><div className="ctx-sep" /><button className="ctx-danger" onClick={() => { closeAllPositions(); setContextMenu(null); }}>Close All</button></>}
                {drawingTools.selectedId != null && <>
                  <div className="ctx-sep" />
                  <button onClick={() => { drawingTools.copyDrawing(drawingTools.selectedId); setContextMenu(null); }}>Copy</button>
                  <button onClick={() => { drawingTools.duplicateDrawing(drawingTools.selectedId); setContextMenu(null); }}>Duplicate</button>
                  <button onClick={() => { drawingTools.openPropsEditor(drawingTools.selectedId); setContextMenu(null); }}>Properties...</button>
                  <button className="ctx-danger" onClick={() => { drawingTools.removeDrawing(drawingTools.selectedId); setContextMenu(null); }}>Delete</button>
                </>}
                <div className="ctx-sep" />
                <button onClick={() => { drawingTools.selectTool('hline'); setContextMenu(null); }}>H-Line</button>
                <button onClick={() => { drawingTools.selectTool('trendline'); setContextMenu(null); }}>Trend Line</button>
                {contextMenu.price && <button onClick={() => { priceAlerts.addAlert(contextMenu.price); setContextMenu(null); }}>Alert @ {contextMenu.price.toFixed(2)}</button>}
                <button onClick={() => { setShowAlertBuilder(true); setContextMenu(null); }}>Multi-Alert...</button>
              </div>
            )}

            {showCorrSelector && session && (
              <div className="corr-selector-overlay">
                <CorrelationSelector currentInstrument={session.instrument} currentTimeframe={session.timeframe}
                  selectedIds={correlationIds} onAdd={(id) => setCorrelationIds(prev => [...prev, id])}
                  onRemove={(id) => setCorrelationIds(prev => prev.filter(x => x !== id))}
                  onClose={() => setShowCorrSelector(false)} />
              </div>
            )}
          </div>

          {/* Bottom panel tabs */}
          <div className="bottom-panel-handle">
            <div className="bp-tabs">
              <button className={`bp-tab ${bottomTab === 'positions' ? 'active' : ''}`} onClick={() => { setBottomTab('positions'); setBottomPanelOpen(true); }}>
                Positions ({openPositions.length})
              </button>
              <button className={`bp-tab ${bottomTab === 'history' ? 'active' : ''}`} onClick={() => { setBottomTab('history'); setBottomPanelOpen(true); }}>
                History ({closedTrades.length})
              </button>
              <button className={`bp-tab ${bottomTab === 'performance' ? 'active' : ''}`} onClick={() => { setBottomTab('performance'); setBottomPanelOpen(true); }}>
                Performance
              </button>
            </div>
            <button className="bp-collapse" onClick={() => setBottomPanelOpen(v => !v)}>{bottomPanelOpen ? '▼' : '▲'}</button>
          </div>

          {bottomPanelOpen && (
            <div className="replay-bottom-panel" style={{ height: 200 }}>
              {(bottomTab === 'positions' || bottomTab === 'history') && (
                <PositionsList openPositions={openPositions} pendingOrders={pendingOrders} closedTrades={closedTrades}
                  currentPrice={currentPrice} sessionId={sessionId}
                  onClosePosition={(id, r, v) => closePosition(id, r || 'manual', v || null)}
                  onModifyPosition={modifyPosition} onCancelPendingOrder={cancelPendingOrder}
                  forceTab={bottomTab === 'history' ? 'closed' : 'open'} />
              )}
              {bottomTab === 'performance' && (
                <PerformanceDashboard startingCapital={parseFloat(session?.starting_capital || 0)}
                  currentCapital={currentCapital} equity={equity}
                  closedTrades={closedTrades} openPositions={openPositions} />
              )}
            </div>
          )}
        </div>

        {/* Right panel: order entry */}
        {rightPanelOpen && (
          <div className="replay-right-panel">
            <OrderPanel onPlaceOrder={handlePlaceOrder} currentPrice={currentPrice}
              spread={session ? parseFloat(session.spread_points) : 0} equity={equity}
              playbooks={playbooksList} onPlaybookChange={setActivePlaybookId} />
          </div>
        )}

        {showAI && <div className="replay-ai-sidebar"><AIAnalysisPanel {...aiAnalysis} /></div>}
      </div>

      {/* Multi-Condition Alert Builder */}
      {showAlertBuilder && (
        <div className="hotkeys-overlay" onClick={() => setShowAlertBuilder(false)}>
          <div className="hotkeys-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h3>Create Multi-Condition Alert</h3>
            <AlertBuilder
              onSave={(label, conditions, logic) => {
                priceAlerts.addMultiAlert(label, conditions, logic);
                setShowAlertBuilder(false);
              }}
              onCancel={() => setShowAlertBuilder(false)}
            />
          </div>
        </div>
      )}

      {/* Strategy Tester */}
      {showStrategyTester && (
        <StrategyTester candles={allCandles} onClose={() => setShowStrategyTester(false)} />
      )}

      {/* Alert notifications */}
      {priceAlerts.triggered.length > 0 && (
        <div className="alert-notifications">
          {priceAlerts.triggered.slice(-3).map((t, i) => (
            <div key={i} className={`alert-notification alert-${t.direction}`}>
              Alert triggered @ {t.price.toFixed(5)} ({t.direction === 'up' ? '▲' : '▼'})
            </div>
          ))}
          <button className="alert-dismiss" onClick={priceAlerts.clearTriggered}>Dismiss</button>
        </div>
      )}

      {/* Hotkeys help overlay */}
      {showHotkeys && (
        <div className="hotkeys-overlay" onClick={() => setShowHotkeys(false)}>
          <div className="hotkeys-panel" onClick={e => e.stopPropagation()}>
            <h3>Keyboard Shortcuts</h3>
            <div className="hotkeys-grid">
              <div className="hotkeys-section">
                <h4>Replay</h4>
                <div className="hotkey-row"><kbd>Space</kbd><span>Play / Pause</span></div>
                <div className="hotkey-row"><kbd>&rarr;</kbd><span>Step forward (1 bar)</span></div>
                <div className="hotkey-row"><kbd>&larr;</kbd><span>Step backward (1 bar)</span></div>
                <div className="hotkey-row"><kbd>Shift+&rarr;</kbd><span>Skip forward (50 bars)</span></div>
                <div className="hotkey-row"><kbd>Shift+&larr;</kbd><span>Skip backward (50 bars)</span></div>
              </div>
              <div className="hotkeys-section">
                <h4>Trading</h4>
                <div className="hotkey-row"><kbd>B</kbd><span>Buy (market order)</span></div>
                <div className="hotkey-row"><kbd>S</kbd><span>Sell (market order)</span></div>
                <div className="hotkey-row"><kbd>X</kbd><span>Close all positions</span></div>
              </div>
              <div className="hotkeys-section">
                <h4>Drawing</h4>
                <div className="hotkey-row"><kbd>Ctrl+Z</kbd><span>Undo</span></div>
                <div className="hotkey-row"><kbd>Ctrl+Y</kbd><span>Redo</span></div>
                <div className="hotkey-row"><kbd>Ctrl+C</kbd><span>Copy drawing</span></div>
                <div className="hotkey-row"><kbd>Ctrl+V</kbd><span>Paste drawing</span></div>
                <div className="hotkey-row"><kbd>Ctrl+D</kbd><span>Duplicate drawing</span></div>
                <div className="hotkey-row"><kbd>Del</kbd><span>Delete selected</span></div>
                <div className="hotkey-row"><kbd>Esc</kbd><span>Cancel / Deselect</span></div>
              </div>
            </div>
            <button className="btn btn-sm" onClick={() => setShowHotkeys(false)} style={{ marginTop: '1rem' }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
